import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';

// Use HashRouter for portable builds (file:// protocol) and BrowserRouter for web
const isPortable = typeof window !== 'undefined' && window.location.protocol === 'file:';
const Router = isPortable ? HashRouter : BrowserRouter;
import { useAcademyStore } from './store/academyStore';
import { ProfileManager } from './components/profile/ProfileManager';
import { AuthScreen } from './components/auth/AuthScreen';
import { UserProfile } from './components/profile/UserProfile';
import { BadgeExportView } from './components/profile/BadgeExportView';
import { UserManual } from './components/dashboard/UserManual';
import { DebugOverlay } from './components/debug/DebugOverlay';
import { DecoderTool } from './components/tools/DecoderTool';

import Dashboard from './components/dashboard/Dashboard';
import { StepWizard } from './components/modules/SheetWizard/StepWizard';
import { TopBar } from './components/layout/TopBar';
import { AuthModule } from './components/modules/Auth/AuthModule';
import { ClientServerMap } from './components/modules/Foundation/Architecture';
import { VariableBackpack } from './components/modules/Foundation/Variables';
import { FunctionRecipe } from './components/modules/Foundation/FunctionRecipe';
import { LoopsArrays } from './components/modules/Foundation/LoopsArrays';
import { InventoryLogic } from './components/modules/Foundation/InventoryLogic';
import { ParrotTalk } from './components/modules/Foundation/ParrotTalk';
import { EncryptionModule } from './components/modules/CyberSecurity/Encryption';
import { SqlInjection } from './components/modules/CyberSecurity/SqlInjection';
import { XssSandbox } from './components/modules/CyberSecurity/XssSandbox';
import { AiPhishing } from './components/modules/CyberSecurity/AiPhishing';
import { DefenseInDepth } from './components/modules/CyberSecurity/DefenseInDepth';
import { AiEncryption } from './components/modules/CyberSecurity/AiEncryption';
import { SecurityUsability } from './components/modules/CyberSecurity/SecurityUsability';
import { LibrarySQL } from './components/modules/DataDetective/LibrarySQL';
import { SqlJoins } from './components/modules/DataDetective/SqlJoins';
import { PromptEngineering } from './components/modules/AiOperator/PromptEngineering';
import { AiAgents } from './components/modules/AiOperator/AiAgents';
import { CityBlueprints } from './components/modules/Production/CityBlueprints';
import { EventDrivenArch } from './components/modules/Automation/EventDrivenArch';
import { WebhookCatcher } from './components/modules/Automation/WebhookCatcher';
import { BoxModel } from './components/modules/UiUx/BoxModel';
import { FlexboxPlayground } from './components/modules/UiUx/FlexboxPlayground';
import { AccessibilityLens } from './components/modules/UiUx/AccessibilityLens';
import { CssGrid } from './components/modules/UiUx/CssGrid';
import { DockerContainers } from './components/modules/SovereignSystems/DockerContainers';
import { StripeCheckout } from './components/modules/SovereignSystems/StripeCheckout';
import { ReverseProxy } from './components/modules/SovereignSystems/ReverseProxy';
import { VectorSearch } from './components/modules/AiOperator/VectorSearch';
import { RealtimeChat } from './components/modules/Production/RealtimeChat';
import { Dashboarding } from './components/modules/DataDetective/Dashboarding';
import { CiaTriad } from './components/modules/CyberSecurity/CiaTriad';
import { OAuthFlow } from './components/modules/Automation/OAuthFlow';

// New modules
import { CrudOperations } from './components/modules/Production/CrudOperations';
import { ShoppingCartDb } from './components/modules/Production/ShoppingCartDb';
import { CronScheduler } from './components/modules/Automation/CronScheduler';
import { ModelParameters } from './components/modules/AiOperator/ModelParameters';
import { FilterSort } from './components/modules/DataDetective/FilterSort';
import { TypographyBasics } from './components/modules/UiUx/TypographyBasics';

// Wave 2 modules
import { DbMigrations } from './components/modules/Production/DbMigrations';
import { PasswordStrength } from './components/modules/CyberSecurity/PasswordStrength';
import { ServerMonitoring } from './components/modules/SovereignSystems/ServerMonitoring';
import { ColorTheory } from './components/modules/UiUx/ColorTheory';
import { GamePolish } from './components/modules/GameDev/GamePolish';
import { ReactComponents } from './components/modules/LogicArchitect/ReactComponents';
import { ReactState } from './components/modules/LogicArchitect/ReactState';
import { ReactEffects } from './components/modules/LogicArchitect/ReactEffects';
import { ReactProps } from './components/modules/LogicArchitect/ReactProps';
import { SqlAggregates } from './components/modules/DataDetective/SqlAggregates';

// Digital Foundations (Course 0)
import { InternetSafety } from './components/modules/DigitalFoundations/InternetSafety';
import { DigitalFootprint } from './components/modules/DigitalFoundations/DigitalFootprint';
import { ResearchSkills } from './components/modules/DigitalFoundations/ResearchSkills';
import { VisualCoding } from './components/modules/DigitalFoundations/VisualCoding';
import { Prerequisites } from './components/modules/DigitalFoundations/Prerequisites';
import { CertificateView } from './components/profile/CertificateView';
import { DiplomaView } from './components/profile/DiplomaView';

// Game Development (Course 9)
import { GameLoop } from './components/modules/GameDev/GameLoop';
import { SpriteCollision } from './components/modules/GameDev/SpriteCollision';
import { ScoreSystem } from './components/modules/GameDev/ScoreSystem';
import { BossLogic } from './components/modules/GameDev/BossLogic';

// Mini-Games
import { VariableTetris, HackDefense, QueryQuest, GamesHub, FlexboxFroggy, LoopRacer, WebhookCatcher as GameWebhookCatcher, FunctionFactory, GridGarden, BumpAndBounce, StateSorter, RegexAsteroids, GridInvaders } from './components/games';
import { GorillaOS } from './components/modules/Capstone/GorillaOS';

// Placeholder wrappers for layout consistency
const SheetWizardWrapper = () => (
  <div className="bg-slate-50 min-h-screen flex flex-col pt-24">
    <div className="flex-1 overflow-auto">
      <StepWizard />
    </div>
  </div>
);

import { GorillaVoiceManager } from './components/accessibility/GorillaVoiceManager';
import { RadioBar } from './components/layout/RadioBar';

import { ErrorBoundary } from 'react-error-boundary';

// ...

const GlobalErrorFallback = ({ error, resetErrorBoundary }: any) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-100 p-8 text-center">
      <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-2xl w-full border-4 border-red-100">
        <h1 className="text-4xl font-black text-slate-800 mb-4 font-comic tracking-tight">
          🙈 Uh oh! The app crashed.
        </h1>
        <p className="text-slate-600 mb-6 text-lg">
          Don't worry, the monkeys are investigating. Here's what went wrong:
        </p>
        <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-100 mb-8 overflow-auto max-h-64 text-left">
          <code className="font-mono text-red-600 text-sm break-all">
            {error.message || JSON.stringify(error)}
          </code>
        </div>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => window.location.href = '/'}
            className="px-8 py-4 bg-slate-800 text-white font-bold rounded-xl hover:bg-slate-900 transition-all hover:scale-105"
          >
            🏠 Go Home
          </button>
          <button
            onClick={resetErrorBoundary}
            className="px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all hover:scale-105"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const { currentUser, logoutUser } = useAcademyStore();

  // Expose store for Playwright E2E testing
  if (typeof window !== 'undefined') {
    // @ts-ignore
    window.useAcademyStore = useAcademyStore;
  }

  React.useEffect(() => {
    if (currentUser && (currentUser.username.toLowerCase().includes('bnesphone') || currentUser.username.includes('@gmail'))) {
      logoutUser();
    }
  }, [currentUser, logoutUser]);

  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      <Router>
        <GorillaVoiceManager />
        <RadioBar />
        <DebugOverlay />
        {!currentUser ? (
          <AuthScreen />
        ) : (
          <>
            <TopBar />
            <Routes>
              {/* ... routes ... */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/sheet-wizard" element={<SheetWizardWrapper />} />
              <Route path="/relational-design" element={<CityBlueprints />} />
              <Route path="/event-driven-arch" element={<EventDrivenArch />} />
              <Route path="/box-model" element={<BoxModel />} />
              <Route path="/docker-containers" element={<DockerContainers />} />
              <Route path="/architecture" element={<ClientServerMap />} />
              <Route path="/variables-types" element={<VariableBackpack />} />
              <Route path="/functions-scope" element={<FunctionRecipe />} />
              <Route path="/loops-arrays" element={<LoopsArrays />} />
              <Route path="/objects-json" element={<ParrotTalk />} />
              <Route path="/cryptography-basics" element={<EncryptionModule />} />
              <Route path="/data-detective-sql" element={<LibrarySQL />} />
              <Route path="/prompt-engineering" element={<PromptEngineering />} />
              <Route path="/rag-basics" element={<VectorSearch />} />
              <Route path="/auth" element={<AuthModule />} />
              <Route path="/flexbox" element={<FlexboxPlayground />} />
              <Route path="/sql-injection" element={<SqlInjection />} />
              <Route path="/ai-agents" element={<AiAgents />} />
              <Route path="/webhooks" element={<WebhookCatcher />} />
              <Route path="/xss" element={<XssSandbox />} />
              <Route path="/accessibility" element={<AccessibilityLens />} />
              <Route path="/sql-joins" element={<SqlJoins />} />
              <Route path="/stripe-integration" element={<StripeCheckout />} />
              <Route path="/realtime-subs" element={<RealtimeChat />} />
              <Route path="/dashboarding" element={<Dashboarding />} />
              <Route path="/css-grid" element={<CssGrid />} />
              <Route path="/reverse-proxies" element={<ReverseProxy />} />
              <Route path="/cia-triad" element={<CiaTriad />} />
              <Route path="/oauth-auth" element={<OAuthFlow />} />
              {/* New Modules */}
              <Route path="/crud-operations" element={<CrudOperations />} />
              <Route path="/cron-scheduler" element={<CronScheduler />} />
              <Route path="/model-parameters" element={<ModelParameters />} />
              <Route path="/filter-sort" element={<FilterSort />} />
              <Route path="/typography-basics" element={<TypographyBasics />} />
              {/* New Modules */}
              <Route path="/inventory-logic" element={<InventoryLogic />} />
              <Route path="/shopping-cart-db" element={<ShoppingCartDb />} />
              <Route path="/ai-phishing" element={<AiPhishing />} />
              <Route path="/defense-in-depth" element={<DefenseInDepth />} />
              <Route path="/ai-encryption" element={<AiEncryption />} />
              <Route path="/security-usability" element={<SecurityUsability />} />
              {/* Wave 2 Modules */}
              <Route path="/db-migrations" element={<DbMigrations />} />
              <Route path="/password-strength" element={<PasswordStrength />} />
              <Route path="/server-monitoring" element={<ServerMonitoring />} />
              <Route path="/color-theory" element={<ColorTheory />} />
              <Route path="/sql-aggregates" element={<SqlAggregates />} />
              {/* Logic Architect (Course 10) */}
              <Route path="/game-polish" element={<GamePolish />} />
              <Route path="/react-components" element={<ReactComponents />} />
              <Route path="/react-state" element={<ReactState />} />
              <Route path="/react-effects" element={<ReactEffects />} />
              <Route path="/react-props" element={<ReactProps />} />
              {/* Digital Foundations (Course 0) */}
              <Route path="/internet-safety" element={<InternetSafety />} />
              <Route path="/digital-footprint" element={<DigitalFootprint />} />
              <Route path="/research-skills" element={<ResearchSkills />} />
              <Route path="/visual-coding" element={<VisualCoding />} />
              {/* Game Development (Course 9) */}
              <Route path="/game-loop" element={<GameLoop />} />
              <Route path="/sprite-collision" element={<SpriteCollision />} />
              <Route path="/score-system" element={<ScoreSystem />} />
              <Route path="/boss-logic" element={<BossLogic />} />
              {/* Mini-Games */}
              <Route path="/games" element={<GamesHub />} />
              <Route path="/games/variable-tetris" element={<VariableTetris />} />
              <Route path="/games/hack-defense" element={<HackDefense />} />
              <Route path="/games/query-quest" element={<QueryQuest />} />
              <Route path="/games/flexbox-froggy" element={<FlexboxFroggy />} />
              <Route path="/games/loop-racer" element={<LoopRacer />} />
              <Route path="/games/function-factory" element={<FunctionFactory />} />
              <Route path="/games/grid-garden" element={<GridGarden />} />
              <Route path="/games/webhook-catcher" element={<GameWebhookCatcher />} />
              <Route path="/games/bump-and-bounce" element={<BumpAndBounce />} />
              <Route path="/games/state-sorter" element={<StateSorter />} />
              <Route path="/games/regex-asteroids" element={<RegexAsteroids />} />
              <Route path="/games/grid-invaders" element={<GridInvaders />} />
              <Route path="/capstone" element={<GorillaOS />} />
              <Route path="/certificate/:courseId" element={<CertificateView />} />
              <Route path="/diploma" element={<DiplomaView />} />
              <Route path="/prerequisites" element={<Prerequisites />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/badges/export" element={<BadgeExportView />} />
              <Route path="/manual" element={<UserManual />} />
              <Route path="/decoder" element={<DecoderTool />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </>
        )}
      </Router>
    </ErrorBoundary>
  );
}

export default App;